<template>
  <div class="rules-tab">
    <div class="info-card">
      <v-form class="rules-form">
        <draggable
          v-model="localRules"
          handle=".drag-handle"
          :animation="200"
          ghost-class="ghost-row"
          drag-class="dragging-row"
          :disabled="isTournamentStarted"
          tag="div"
          @end="onDragEnd"
        >
          <div
            v-for="(row, index) in sortedRules"
            :key="row.id"
            class="rule-item"
          >
            <!-- Left: drag + number -->
            <div class="rule-left">
              <div class="drag-handle" :class="{ 'disabled': isTournamentStarted }">
                <i class="fa-regular fa-grip-vertical"></i>
              </div>
              <div class="rule-index">{{ index + 1 }}º</div>
            </div>

            <!-- Content -->
            <div class="rule-content">
              <div class="top-row">
                <!-- Rule select -->
                <div class="field-group">
                  <label class="field-title">
                    {{$t('rule')}} <span class="required">*</span>
                  </label>

                  <v-select
                    v-model="row.type"
                    :items="getAvailableRuleOptionsForRow(row)"
                    item-text="label"
                    item-value="type"
                    dense
                    outlined
                    hide-details="auto"
                    class="rule-select-input"
                    :menu-props="menuProps"
                    :placeholder="$t('selectRulePlaceholder')"
                    :error-messages="getErrorMessage(row, 'type')"
                    :disabled="isTournamentStarted"
                    @change="onRowTypeChange(row)"
                  >
                    <template v-slot:item="{ item }">
                      <div class="rule-dd-item">
                        <div class="rule-dd-item__title">{{ item.label }}</div>
                        <div class="rule-dd-item__desc">{{ item.description }}</div>
                      </div>
                    </template>

                    <template v-slot:selection="{ item }">
                      <span>{{ item.label }}</span>
                    </template>
                  </v-select>
                </div>

                <!-- Value + actions (igual a prizes) -->
                <div class="value-actions">
                  <div class="field-group">
                    <label class="field-title">
                      {{$t('value')}}
                      <span v-if="row.type && !isToggleType(row.type)" class="required">*</span>
                    </label>

                    <!-- si es toggle: muestro “Toggle” (o puedes ocultarlo) -->
                    <v-text-field
                      v-if="row.type && !isToggleType(row.type)"
                      v-model="row.value"
                      :type="getInputType(row.type)"
                      outlined
                      dense
                      hide-details="auto"
                      class="value-input"
                      :prefix="getPrefix(row.type)"
                      :suffix="getSuffix(row.type)"
                      :error-messages="getErrorMessage(row, 'value')"
                      :disabled="isTournamentStarted"
                      @input="onValueInput(row)"
                      @blur="onValueBlur(row)"
                    />

                    <div v-else class="toggle-placeholder">
                      —
                    </div>
                  </div>

                  <div class="row-actions">
                    <v-btn
                      v-if="index > 0"
                      icon
                      small
                      elevation="0"
                      class="icon-btn delete"
                      :disabled="isTournamentStarted"
                      @click="removeRow(index)"
                    >
                      <i class="fa-regular fa-trash"></i>
                    </v-btn>

                    <v-btn
                      v-if="index === sortedRules.length - 1 && hasAvailableRules"
                      icon
                      small
                      elevation="0"
                      class="icon-btn add"
                      :disabled="isTournamentStarted"
                      @click="addRow"
                    >
                      <i class="fa-regular fa-plus"></i>
                    </v-btn>
                  </div>
                </div>
              </div>
            </div>

            <!-- Separator -->
            <div v-if="index !== sortedRules.length - 1" class="separator">
              <span class="separator-plus">+</span>
            </div>
          </div>
        </draggable>
      </v-form>
    </div>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import { mapState, mapMutations } from "vuex";
import Repository from "../../../../repositories/tournament";

export default {
  name: "RulesTab",
  components: { draggable },
  props: {
    setNextBtnDisabled: { type: Function, required: false }
  },
  data() {
    return {
      menuProps: {
        offsetY: true,
        closeOnClick: true,
        closeOnContentClick: true,
        maxHeight: 360,
        // importante: en algunos layouts esto evita que el menú quede “tapado”
        // si te queda raro, lo puedes quitar:
        // attach: "body",
      },
    };
  },
  computed: {
    tournamentSummary() {
      return this.$store.state.tournament;
    },
    isTournamentStarted() {
      if (!this.tournamentSummary || !this.tournamentSummary.startTime) return false;
      const now = new Date();
      const startTime = new Date(this.tournamentSummary.startTime);
      return now >= startTime;
    },
    ...mapState({
      rules: (s) => s.tournamentForm.rules, // array en store
    }),

    localRules: {
      get() {
        return this.rules;
      },
      set(v) {
        this.updateTournamentRules(v);
      },
    },

    sortedRules() {
      return [...(this.localRules || [])].sort((a, b) => (a.position || 1) - (b.position || 1));
    },

    ruleOptions() {
      return [
        {
          type: "CONSISTENCY",
          label: this.$t("consistencyRuleLabel"),
          description: this.$t("consistencyRuleDescription"),
        },
        {
          type: "MAX_DRAWDOWN_BL_PT",
          label: this.$t("maxDrawdownBalanceRuleLabel"),
          description: this.$t("maxDrawdownBalanceRuleDescription"),
        },
        {
          type: "MAX_DRAWDOWN_EQ_PT",
          label: this.$t("maxDrawdownEquityRuleLabel"),
          description: this.$t("maxDrawdownEquityRuleDescription"),
        },
        {
          type: "MAX_TRADE_SIZE",
          label: this.$t("maxTradeSizeRuleLabel"),
          description: this.$t("maxTradeSizeRuleDescription"),
        },
        {
          type: "OVERNIGHT",
          label: this.$t("overnightRuleLabel"),
          description: this.$t("overnightRuleDescription"),
        },
        {
          type: "POSITIVE_BALANCE",
          label: this.$t("positiveBalanceRuleLabel"),
          description: this.$t("positiveBalanceRuleDescription"),
        },
        {
          type: "POSITIVE_EQUITY",
          label: this.$t("positiveEquityRuleLabel"),
          description: this.$t("positiveEquityRuleDescription"),
        },
        {
          type: "MIN_TRADABLE_DAYS",
          label: this.$t("minTradableDaysRuleLabel"),
          description: this.$t("minTradableDaysRuleDescription"),
        },
        {
          type: "MIN_TRADES",
          label: this.$t("minTradesRuleLabel"),
          description: this.$t("minTradesRuleDescription"),
        }
      ];
    },


    availableRuleOptions() {
      // Get all selected rule types (excluding the current row being edited)
      const selectedTypes = this.sortedRules
        .filter(rule => rule.type) // Only include rules that have a type selected
        .map(rule => rule.type);
      
      // Filter out already selected rules
      return this.ruleOptions.filter(option => !selectedTypes.includes(option.type));
    },

    hasAvailableRules() {
      // Check if there are any rules available to add and limit to 6 rules maximum
      return this.availableRuleOptions.length > 0 && this.sortedRules.length < 6;
    },
  },
  methods: {
    ...mapMutations(["updateTournamentRules", "setTournament"]),

    init() {
      if (!this.tournamentSummary || !this.tournamentSummary.tournamentId) {
        // If no tournament, add one empty rule
        if (!this.rules || this.rules.length === 0) {
          this.addRow();
        }
        // Store initial values after rules are set
        this.$nextTick(() => {
          this.saveInitialValues();
        });
        return;
      }

      // Fetch rules from API
      Repository.getTournamentRules(this.tournamentSummary.tournamentId).then((response) => {
        if (response.status < 400 && response.data) {
          // Map API rules to tournamentForm format
          const apiRules = response.data || [];
          const mappedRules = apiRules.map((rule, index) => {
            // Parse value - for percentage types, convert from decimal (0.3) to percentage (30)
            // For other types, use value as-is
            let parsedValue = rule.value;
            if (rule.ruleType === 'CONSISTENCY' || rule.ruleType === 'MAX_DRAWDOWN_RT' || rule.ruleType === 'MAX_DRAWDOWN_BL_PT' || rule.ruleType === 'MAX_DRAWDOWN_EQ_PT') {
              // Convert from decimal format (0.3) to percentage (30) for display
              parsedValue = (rule.value * 100).toString();
            } else if (rule.ruleType === 'OVERNIGHT' || rule.ruleType === 'POSITIVE_BALANCE' || rule.ruleType === 'POSITIVE_EQUITY') {
              // Toggle types - value might be 0 or 1, but we don't need to display it
              parsedValue = '';
            } else {
              // Other types - use value as string
              parsedValue = rule.value ? rule.value.toString() : '';
            }

            return {
              id: this.generateId(),
              position: index + 1,
              type: rule.ruleType || null,
              value: parsedValue,
              isDirty: { type: false, value: false },
              errors: {},
            };
          });

          // Update tournamentForm rules
          if (mappedRules.length > 0) {
            this.updateTournamentRules(mappedRules);
          } else {
            // If no rules, add one empty rule
            this.addRow();
          }

          // Store initial values for change detection after rules are updated
          this.$nextTick(() => {
            this.saveInitialValues();
            this.validateAll();
          });
        } else {
          // If API call fails, add one empty rule
          if (!this.rules || this.rules.length === 0) {
            this.addRow();
          }
          this.$nextTick(() => {
            this.saveInitialValues();
          });
        }
      }).catch(() => {
        // If API call fails, add one empty rule
        if (!this.rules || this.rules.length === 0) {
          this.addRow();
        }
        this.$nextTick(() => {
          this.saveInitialValues();
        });
      });
    },

    saveInitialValues() {
      // Create a normalized copy of rules for comparison (without isDirty and errors)
      const normalizedRules = (this.rules || []).map(rule => ({
        position: rule.position,
        type: rule.type || null,
        value: (rule.value || '').toString().trim(),
      }));
      // Sort by position to ensure consistent comparison
      normalizedRules.sort((a, b) => (a.position || 0) - (b.position || 0));
      this.initialValues = JSON.stringify(normalizedRules);
    },

    checkForChanges() {
      if (!this.initialValues) {
        this.setNextBtnDisabled?.(true);
        return;
      }

      // Create a normalized copy of current rules for comparison (without isDirty and errors)
      const normalizedRules = (this.rules || []).map(rule => ({
        position: rule.position,
        type: rule.type || null,
        value: (rule.value || '').toString().trim(),
      }));
      // Sort by position to ensure consistent comparison
      normalizedRules.sort((a, b) => (a.position || 0) - (b.position || 0));
      const currentValues = JSON.stringify(normalizedRules);
      
      const hasChanges = currentValues !== this.initialValues;
      const isValid = this.validateAll();

      // Disable if no changes or form is invalid
      this.setNextBtnDisabled?.(!hasChanges || !isValid);
    },

    generateId() {
      return `rule-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    },

    normalizePositions(rows) {
      return rows.map((r, idx) => ({
        ...r,
        id: r.id || this.generateId(),
        position: idx + 1,
      }));
    },

    addRow() {
      const current = [...(this.rules || [])];
      // Prevent adding more than 6 rules
      if (current.length >= 6) {
        return;
      }
      const newRow = {
        id: this.generateId(),
        position: current.length + 1,
        type: null,
        value: "",
        isDirty: { type: false, value: false },
        errors: {},
      };
      const updated = this.normalizePositions([...current, newRow]);
      this.updateTournamentRules(updated);
      this.$nextTick(() => {
        this.validateAll();
        this.checkForChanges();
      });
    },

    removeRow(index) {
      const current = [...this.sortedRules];
      current.splice(index, 1);
      const updated = this.normalizePositions(current);
      this.updateTournamentRules(updated);
      this.$nextTick(() => {
        this.validateAll();
        this.checkForChanges();
      });
    },

    onDragEnd() {
      const updated = this.normalizePositions([...this.sortedRules]);
      this.updateTournamentRules(updated);
      this.$nextTick(() => {
        this.validateAll();
        this.checkForChanges();
      });
    },

    onRowTypeChange(row) {
      row.isDirty.type = true;

      // default value por tipo (si aplica)
      if (row.type && !this.isToggleType(row.type)) {
        if (row.value === "" || row.value === null || row.value === undefined) {
          row.value = this.getDefaultValue(row.type);
        }
      } else {
        // toggle => sin value
        row.value = "";
      }

      this.validateRow(row);
      this.updateTournamentRules(this.normalizePositions([...this.sortedRules]));
      this.$nextTick(() => {
        this.validateAll();
        this.checkForChanges();
      });
    },

    onValueInput(row) {
      row.isDirty.value = true;

      // For percentage types, keep the value as 1-100 (don't convert to decimal)
      // The conversion to decimal will happen when saving

      this.validateRow(row);
      this.updateTournamentRules(this.normalizePositions([...this.sortedRules]));
      this.validateAll();
      this.checkForChanges();
    },

    onValueBlur(row) {
      row.isDirty.value = true;
      this.validateRow(row);
      this.updateTournamentRules(this.normalizePositions([...this.sortedRules]));
      this.validateAll();
      this.checkForChanges();
    },

    getErrorMessage(row, field) {
      return row.isDirty?.[field] ? row.errors?.[field] || "" : "";
    },

    validateAll() {
      const rows = this.sortedRules || [];
      if (!rows.length) {
        return false;
      }

      let ok = true;
      for (const r of rows) {
        if (!this.validateRow(r)) ok = false;
      }

      return ok;
    },

    validateRow(row) {
      const errors = {};

      if (!row.type) errors.type = this.$t?.("errors.required") || "Required";

      if (row.type && !this.isToggleType(row.type)) {
        if (row.value === "" || row.value === null || row.value === undefined) {
          errors.value = this.$t?.("errors.required") || "Required";
        } else {
          const n = parseFloat(row.value);
          if (isNaN(n)) errors.value = this.$t?.("errors.invalidNumber") || "Invalid number";
          else if (this.isPercentageType(row.type)) {
            // Allow values from 0 to 90%
            if (n < 0 || n > 90) errors.value = this.$t?.("errors.percentageRange") || "Must be between 0 and 90";
          } else if (n <= 0) {
            errors.value = this.$t?.("errors.greaterThanZero") || "Must be > 0";
          }
        }
      }

      row.errors = errors;
      return Object.keys(errors).length === 0;
    },

    // ---- helpers ----
    getAvailableRuleOptionsForRow(row) {
      // Get all selected rule types excluding the current row
      const selectedTypes = this.sortedRules
        .filter(rule => rule.type && rule.id !== row.id) // Exclude current row and only include rules with type
        .map(rule => rule.type);
      
      // Filter out already selected rules, but always include the current row's selected type
      return this.ruleOptions.filter(option => 
        !selectedTypes.includes(option.type) || option.type === row.type
      );
    },

    isToggleType(type) {
      return ["OVERNIGHT", "POSITIVE_BALANCE", "POSITIVE_EQUITY"].includes(type);
    },
    isPercentageType(type) {
      return ["CONSISTENCY", "MAX_DRAWDOWN_RT", "MAX_DRAWDOWN_BL_PT", "MAX_DRAWDOWN_EQ_PT"].includes(type);
    },
    getInputType(type) {
      return "number";
    },
    getPrefix(type) {
      return type === "INITIAL_BALANCE" || type === "MAX_DRAWDOWN" ? "$" : "";
    },
    getSuffix(type) {
      return this.isPercentageType(type) ? "%" : "";
    },
    getDefaultValue(type) {
      if (type === "INITIAL_BALANCE") return "1000";
      if (type === "CONSISTENCY") return "30";      // 30% (display as percentage)
      if (type === "MAX_DRAWDOWN_RT") return "30";  // 30% (display as percentage)
      if (type === "MAX_DRAWDOWN_BL_PT") return "30";  // 30% (display as percentage)
      if (type === "MAX_DRAWDOWN_EQ_PT") return "30";  // 30% (display as percentage)
      if (type === "MAX_DRAWDOWN") return "1000";
      if (type === "MAX_TRADE_SIZE") return "1";
      if (type === "MIN_TRADABLE_DAYS") return "1";
      if (type === "MIN_TRADES") return "1";
      return "";
    },

    saveTournamentSettings() {
      // Validate that tournament exists in store
      if (!this.tournamentSummary || !this.tournamentSummary.tournamentId) {
        this.$emit('save-error', {
          message: this.$t('alerts.errorOccurred') || 'Tournament data not available'
        });
        return;
      }

      // Validate all rules before saving
      if (!this.validateAll()) {
        this.$emit('save-error', {
          message: this.$t('alerts_TournamentSettings.invalidForm') || 'Please fill all required fields'
        });
        return;
      }

      // Transform rules from frontend format to backend format
      const tournamentRules = this.sortedRules.map(rule => {
        let ruleValue;

        if (this.isToggleType(rule.type)) {
          // For toggle types (OVERNIGHT, POSITIVE_BALANCE, POSITIVE_EQUITY), send 1 if rule is selected
          ruleValue = 1;
        } else if (this.isPercentageType(rule.type)) {
          // For percentage types, convert from percentage (30) to decimal (0.3)
          const percentageValue = parseFloat(rule.value);
          if (!isNaN(percentageValue)) {
            ruleValue = percentageValue / 100;
          } else {
            ruleValue = 0;
          }
        } else if (rule.type === 'MIN_TRADABLE_DAYS' || rule.type === 'MIN_TRADES') {
          // Integer: minimum tradable days / minimum trades
          ruleValue = parseInt(rule.value, 10) || 0;
        } else {
          // For other types, use value as number
          ruleValue = parseFloat(rule.value) || 0;
        }

        return {
          ruleType: rule.type,
          value: ruleValue
        };
      });

      const rulesData = {
        tournamentId: this.tournamentSummary.tournamentId,
        rules: tournamentRules
      };

      Repository.editTournamentRules(rulesData).then((response) => {
        if (response.status < 400 && response.data) {
          // Update initial values after successful save
          this.saveInitialValues();
          
          // Disable save button after successful save
          if (this.setNextBtnDisabled) {
            this.setNextBtnDisabled(true);
          }

          // Refresh tournament data and update store
          Repository.getTournamentOverview(this.tournamentSummary.tournamentId).then((response) => {
            if (response.status < 400) {
              this.setTournament(response.data);
            }
          });

          // Emit success event
          this.$emit('save-success', {
            message: this.$t('alerts_TournamentSettings.edited') || 'Rules updated successfully'
          });
        } else {
          this.$emit('save-error', {
            message: this.$t('alerts.errorOccurred') || 'Error updating rules'
          });
        }
      }).catch((error) => {
        this.$emit('save-error', {
          message: error.response?.data?.message || this.$t('alerts.errorOccurred') || 'Error updating rules'
        });
      });
    },
  },
  mounted() {
    if (this.tournamentSummary) {
      this.init();
    }
    // Disable save button initially
    if (this.setNextBtnDisabled) {
      this.setNextBtnDisabled(true);
    }
  },
  watch: {
    tournamentSummary: {
      handler(newVal) {
        if (newVal && !this.initialValues) {
          this.init();
        }
      },
      immediate: true
    }
  },
};
</script>

<style lang="scss" scoped>
.info-card {
  background-color: var(--v-cardBackground-base);
  border: 1px solid #ededed;
  border-radius: 10px;
  padding: 24px 24px 18px;
  min-height: 520px; /* ajusta: 480–560 según tu row height */
  display: flex;
  flex-direction: column;

}

.theme--dark .info-card {
  border-color: #424242;
}
.rules-form {
  flex: 1;
}

/* misma estructura que prizes */
.rule-item {
  display: grid;
  grid-template-columns: 64px 1fr;
  gap: 12px;
  align-items: start;
  padding: 12px 0;
}

.rule-left {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 24px;
}

.drag-handle {
  cursor: grab;
  opacity: 0.6;

  &.disabled {
    cursor: not-allowed;
    opacity: 0.3;
  }

  i {
    color: #9a9a9a;
    font-size: 16px;
  }

  &:active {
    cursor: grabbing;
  }
}

.rule-index {
  font-weight: 700;
  color: var(--v-textPrimary-base);
}

.rule-content {
  min-width: 0;
}

.top-row {
  display: grid;
  grid-template-columns: 1fr 340px; /* select + value/actions */
  gap: 12px;
  align-items: start;
}

/* value + buttons (igual que prizes) */
.value-actions {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px;
  align-items: end;
}

.row-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding-bottom: 6px;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-title {
  font-family: "Avenir", sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: var(--v-textPrimary-base);
}

.required {
  color: red;
  margin-left: 2px;
}

/* dropdown item con descripción */
.rule-dd-item {
  padding: 6px 0;
}
.rule-dd-item__title {
  font-weight: 700;
  color: var(--v-textPrimary-base);
  font-size: 13px;
}
.rule-dd-item__desc {
  font-size: 12px;
  color: #8d8d8d;
  line-height: 1.25;
  margin-top: 2px;
}

/* placeholder para toggles */
.toggle-placeholder {
  height: 40px;
  display: flex;
  align-items: center;
  color: #9a9a9a;
  border: 1px solid #d7d7d7;
  border-radius: 10px;
  padding: 0 12px;
}

/* Buttons */
.icon-btn {
  width: 38px !important;
  height: 38px !important;
  border-radius: 10px !important;
  min-width: 38px !important;
}

.icon-btn.delete {
  background: #9a9a9a !important;
  transition: background 0.2s ease;

  i {
    color: white;
  }

  &:hover {
    background: #757575 !important;
  }
}

.icon-btn.add {
  background: var(--v-mainGreen-base) !important;
  transition: background 0.2s ease;

  i {
    color: #111;
  }

  &:hover {
    background: #20c77d !important;
  }
}

/* Separator */
.separator {
  grid-column: 1 / -1;
  position: relative;
  margin: 14px 0 6px;
  height: 1px;
  background: #d7d7d7;
}

.separator-plus {
  position: absolute;
  left: 50%;
  top: -10px;
  transform: translateX(-50%);
  background: var(--v-cardBackground-base);
  padding: 0 8px;
  color: #9a9a9a;
  font-weight: 700;
}

/* para que inputs queden consistentes */
::v-deep .value-input .v-input__slot,
::v-deep .rule-select-input .v-input__slot {
  min-height: 40px !important;
}

// Estilos para campos deshabilitados con soporte para dark mode
.theme--light .rules-tab {
  --disabled-bg: #f5f5f5;
  --disabled-border: #e0e0e0;
  --disabled-text: #9e9e9e;
  --disabled-icon: #9e9e9e;
}

.theme--dark .rules-tab {
  --disabled-bg: rgba(255, 255, 255, 0.06);
  --disabled-border: rgba(255, 255, 255, 0.14);
  --disabled-text: rgba(255, 255, 255, 0.55);
  --disabled-icon: rgba(255, 255, 255, 0.45);
}

::v-deep .v-input--is-disabled .v-input__control .v-input__slot {
  background-color: var(--disabled-bg) !important;
  border-color: var(--disabled-border) !important;
}

::v-deep .v-input--is-disabled input,
::v-deep .v-input--is-disabled textarea {
  color: var(--disabled-text) !important;
  -webkit-text-fill-color: var(--disabled-text) !important;
}

::v-deep .v-input--is-disabled .v-label {
  color: var(--disabled-text) !important;
}

::v-deep .v-input--is-disabled .v-select__selection {
  color: var(--disabled-text) !important;
}

::v-deep .v-input--is-disabled .v-icon {
  color: var(--disabled-icon) !important;
}

::v-deep .v-input--is-disabled .v-input__details .v-messages .v-messages__message {
  color: var(--disabled-text) !important;
}

::v-deep .v-input--is-disabled .v-input__details .v-counter {
  color: var(--disabled-text) !important;
}
</style>
